import { useCallback, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';

import type { GraphDoc, GraphGroup, GraphNode } from '../../state/graph.js';
import './graph.css';

/**
 * Reusable node-graph editor shared by the Visual Script and Shader Graph
 * panels. State-free: the owner passes the document and receives replacements
 * (`history: false` while a drag continues, so one drag is one undo step).
 *
 * Pan: middle-drag / Alt-drag / Space-drag. Zoom: wheel. Box select: drag on
 * empty canvas (Shift adds). Wires: drag from a pin to a compatible pin.
 * Search menu: double-click the canvas or press Tab. Copy / paste / duplicate:
 * Ctrl+C / V / D. Delete: Del. Undo/redo: Ctrl+Z / Ctrl+Y (owner callbacks).
 */

export interface PinDef {
  name: string;
  kind: 'exec' | 'data';
  default?: string;
}
export interface NodeDef {
  type: string;
  category: string;
  event?: boolean;
  execIn?: boolean;
  ins: { name: string; default: string }[];
  outs: string[];
  execs?: string[];
  custom?: boolean;
}

/** Node settings edited in the node body (stored in `node.data`). */
export const NODE_SETTINGS: Record<string, string[]> = {
  get_var: ['var'],
  set_var: ['var'],
  compare: ['op'],
  literal: ['value'],
  call: ['fn', 'args'],
  call_pure: ['fn', 'args'],
  property: ['name'],
  texture: ['texture'],
  triplanar: ['texture'],
  float: ['value'],
  vector: ['value'],
};

const W = 190;
const HEADER = 24;
const ROW = 22;
const SET_H = 22;

export function pinsOf(def: NodeDef | undefined, node: GraphNode): { left: PinDef[]; right: PinDef[] } {
  if (!def) return { left: [], right: [] };
  let ins = def.ins;
  if (def.custom) {
    const args = Array.isArray(node.data.args) ? (node.data.args as unknown[]).map(String) : [];
    ins = args.map((a) => ({ name: a, default: '()' }));
  }
  const left: PinDef[] = [];
  if (def.execIn) left.push({ name: 'in', kind: 'exec' });
  for (const i of ins) left.push({ name: i.name, kind: 'data', default: i.default });
  const right: PinDef[] = [...(def.execs ?? []).map((n): PinDef => ({ name: n, kind: 'exec' })), ...def.outs.map((n): PinDef => ({ name: n, kind: 'data' }))];
  return { left, right };
}

function settingsOf(node: GraphNode): string[] {
  return NODE_SETTINGS[node.type] ?? [];
}

function nodeHeight(def: NodeDef | undefined, node: GraphNode): number {
  const { left, right } = pinsOf(def, node);
  return HEADER + settingsOf(node).length * SET_H + Math.max(left.length, right.length, 1) * ROW + 6;
}

function anchor(def: NodeDef | undefined, node: GraphNode, side: 'left' | 'right', pin: string): [number, number] {
  const { left, right } = pinsOf(def, node);
  const list = side === 'left' ? left : right;
  const i = Math.max(0, list.findIndex((p) => p.name === pin));
  return [node.pos[0] + (side === 'left' ? 0 : W), node.pos[1] + HEADER + settingsOf(node).length * SET_H + i * ROW + ROW / 2];
}

const uid = (prefix: string, used: Set<string>): string => {
  let i = used.size + 1;
  while (used.has(`${prefix}${i}`)) i++;
  return `${prefix}${i}`;
};

function parseValue(text: string): unknown {
  const t = text.trim();
  if (t === '') return undefined;
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}
function showValue(v: unknown): string {
  return v === undefined ? '' : typeof v === 'string' ? v : JSON.stringify(v);
}

interface Props {
  doc: GraphDoc;
  defs: NodeDef[];
  /** Node ids with compile errors (red outline) and their messages. */
  errors?: Map<string, string>;
  /** Node ids to highlight as executing (game trace). */
  active?: ReadonlySet<string>;
  onChange: (doc: GraphDoc, options?: { history?: boolean }) => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

type Drag =
  | { kind: 'pan'; sx: number; sy: number; ox: number; oy: number }
  | { kind: 'box'; x0: number; y0: number; x1: number; y1: number; add: boolean }
  | { kind: 'move'; x0: number; y0: number; started: boolean; base: GraphDoc; groupNodes: string[] }
  | { kind: 'wire'; from: string; side: 'left' | 'right'; ax: number; ay: number; x: number; y: number };

export function GraphEditor({ doc, defs, errors, active, onChange, onUndo, onRedo }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ x: 40, y: 40, k: 1 });
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [drag, setDrag] = useState<Drag | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; q: string } | null>(null);
  const clip = useRef<{ nodes: GraphNode[]; edges: GraphDoc['edges'] } | null>(null);
  const spaceDown = useRef(false);

  const byType = useMemo(() => new Map(defs.map((d) => [d.type, d])), [defs]);
  const byId = useMemo(() => new Map(doc.nodes.map((n) => [n.id, n])), [doc.nodes]);

  const toWorld = useCallback(
    (cx: number, cy: number): [number, number] => {
      const r = ref.current?.getBoundingClientRect();
      return [(cx - (r?.left ?? 0) - view.x) / view.k, (cy - (r?.top ?? 0) - view.y) / view.k];
    },
    [view],
  );

  const set = (patch: Partial<GraphDoc>, history = true) => onChange({ ...doc, ...patch }, { history });
  const setNode = (id: string, fn: (n: GraphNode) => GraphNode) => set({ nodes: doc.nodes.map((n) => (n.id === id ? fn(n) : n)) });

  function addNode(def: NodeDef, at: [number, number], connectFrom?: string) {
    const id = uid('n', new Set(doc.nodes.map((n) => n.id)));
    const node: GraphNode = { id, type: def.type, pos: [Math.round(at[0]), Math.round(at[1])], data: {} };
    set({ nodes: [...doc.nodes, node] });
    setSel(new Set([id]));
    void connectFrom;
  }

  function connect(a: string, aSide: 'left' | 'right', b: string, bSide: 'left' | 'right') {
    if (aSide === bSide) return;
    const [from, to] = aSide === 'right' ? [a, b] : [b, a];
    const [fn, fp] = from.split('.');
    const [tn, tp] = to.split('.');
    if (!fn || !tn || fn === tn) return;
    const fd = pinsOf(byType.get(byId.get(fn)?.type ?? ''), byId.get(fn)!);
    const td = pinsOf(byType.get(byId.get(tn)?.type ?? ''), byId.get(tn)!);
    const fk = fd.right.find((p) => p.name === fp)?.kind;
    const tk = td.left.find((p) => p.name === tp)?.kind;
    if (!fk || fk !== tk) return;
    // A data input takes one wire; an exec output continues to one node.
    const edges = doc.edges.filter((e) => (fk === 'data' ? e.to !== to : e.from !== from)).concat({ from, to });
    set({ edges });
  }

  function deleteSelection() {
    if (!sel.size) return;
    set({
      nodes: doc.nodes.filter((n) => !sel.has(n.id)),
      edges: doc.edges.filter((e) => !sel.has(e.from.split('.')[0]!) && !sel.has(e.to.split('.')[0]!)),
      groups: doc.groups.filter((g) => !sel.has(g.id)),
    });
    setSel(new Set());
  }

  function copy() {
    const nodes = doc.nodes.filter((n) => sel.has(n.id));
    const ids = new Set(nodes.map((n) => n.id));
    clip.current = { nodes, edges: doc.edges.filter((e) => ids.has(e.from.split('.')[0]!) && ids.has(e.to.split('.')[0]!)) };
  }

  function paste(offset = 30) {
    const c = clip.current;
    if (!c?.nodes.length) return;
    const used = new Set(doc.nodes.map((n) => n.id));
    const map = new Map<string, string>();
    const nodes = c.nodes.map((n) => {
      const id = uid('n', used);
      used.add(id);
      map.set(n.id, id);
      return { ...n, id, pos: [n.pos[0] + offset, n.pos[1] + offset] as [number, number], data: { ...n.data } };
    });
    const re = (p: string) => `${map.get(p.split('.')[0]!)}.${p.split('.')[1]}`;
    set({ nodes: [...doc.nodes, ...nodes], edges: [...doc.edges, ...c.edges.map((e) => ({ from: re(e.from), to: re(e.to) }))] });
    setSel(new Set(map.values()));
  }

  function addGroup(kind: GraphGroup['kind']) {
    const picked = doc.nodes.filter((n) => sel.has(n.id));
    const used = new Set(doc.groups.map((g) => g.id));
    let pos: [number, number] = toWorld((ref.current?.getBoundingClientRect().left ?? 0) + 200, (ref.current?.getBoundingClientRect().top ?? 0) + 120);
    let size: [number, number] = kind === 'comment' ? [220, 70] : [320, 200];
    if (kind === 'group' && picked.length) {
      const x0 = Math.min(...picked.map((n) => n.pos[0])) - 16;
      const y0 = Math.min(...picked.map((n) => n.pos[1])) - 40;
      const x1 = Math.max(...picked.map((n) => n.pos[0] + W)) + 16;
      const y1 = Math.max(...picked.map((n) => n.pos[1] + nodeHeight(byType.get(n.type), n))) + 16;
      pos = [x0, y0];
      size = [x1 - x0, y1 - y0];
    }
    set({ groups: [...doc.groups, { id: uid('g', used), kind, pos, size, text: kind === 'group' ? 'Group' : 'Comment' }] });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    const typing = (e.target as HTMLElement).tagName === 'INPUT';
    if (e.key === ' ') spaceDown.current = true;
    if (typing) return;
    const mod = e.ctrlKey || e.metaKey;
    if (e.key === 'Delete' || e.key === 'Backspace') deleteSelection();
    else if (mod && e.key === 'c') copy();
    else if (mod && e.key === 'v') paste();
    else if (mod && e.key === 'd') {
      copy();
      paste();
    } else if (mod && e.key === 'a') {
      e.preventDefault();
      setSel(new Set([...doc.nodes.map((n) => n.id), ...doc.groups.map((g) => g.id)]));
    } else if (mod && e.key === 'z') onUndo?.();
    else if (mod && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) onRedo?.();
    else if (e.key === 'Tab') {
      e.preventDefault();
      const r = ref.current!.getBoundingClientRect();
      setMenu({ x: r.width / 2 - 120, y: 60, q: '' });
    } else return;
    e.preventDefault();
  }

  function onPointerDown(e: RPointerEvent) {
    const t = e.target as HTMLElement;
    ref.current?.focus();
    const pin = t.closest<HTMLElement>('[data-pin]');
    if (pin) {
      const [id, name] = pin.dataset.pin!.split('|') as [string, string];
      const side = pin.dataset.side as 'left' | 'right';
      const node = byId.get(id)!;
      const [x, y] = anchor(byType.get(node.type), node, side, name);
      setDrag({ kind: 'wire', from: `${id}.${name}`, side, ax: x, ay: y, x, y });
      ref.current?.setPointerCapture(e.pointerId);
      e.stopPropagation();
      return;
    }
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
    const body = t.closest<HTMLElement>('[data-node]');
    const head = t.closest<HTMLElement>('[data-group]');
    const id = body?.dataset.node ?? head?.dataset.group;
    if (id) {
      const next = new Set(sel);
      if (e.shiftKey) next.has(id) ? next.delete(id) : next.add(id);
      else if (!next.has(id)) {
        next.clear();
        next.add(id);
      }
      setSel(next);
      const [x0, y0] = toWorld(e.clientX, e.clientY);
      const groupNodes: string[] = [];
      for (const g of doc.groups) {
        if (!next.has(g.id) || g.kind !== 'group') continue;
        for (const n of doc.nodes) {
          if (n.pos[0] >= g.pos[0] && n.pos[1] >= g.pos[1] && n.pos[0] + W <= g.pos[0] + g.size[0] && n.pos[1] <= g.pos[1] + g.size[1]) groupNodes.push(n.id);
        }
      }
      setDrag({ kind: 'move', x0, y0, started: false, base: doc, groupNodes });
      ref.current?.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button === 1 || e.altKey || spaceDown.current) {
      setDrag({ kind: 'pan', sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y });
    } else {
      const [x, y] = toWorld(e.clientX, e.clientY);
      if (!e.shiftKey) setSel(new Set());
      setDrag({ kind: 'box', x0: x, y0: y, x1: x, y1: y, add: e.shiftKey });
    }
    ref.current?.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: RPointerEvent) {
    if (!drag) return;
    const [x, y] = toWorld(e.clientX, e.clientY);
    if (drag.kind === 'pan') setView({ ...view, x: drag.ox + e.clientX - drag.sx, y: drag.oy + e.clientY - drag.sy });
    else if (drag.kind === 'box') setDrag({ ...drag, x1: x, y1: y });
    else if (drag.kind === 'wire') setDrag({ ...drag, x, y });
    else if (drag.kind === 'move') {
      const dx = Math.round(x - drag.x0);
      const dy = Math.round(y - drag.y0);
      if (!drag.started && Math.abs(dx) + Math.abs(dy) < 3) return;
      const moved = new Set([...sel, ...drag.groupNodes]);
      onChange(
        {
          ...drag.base,
          nodes: drag.base.nodes.map((n) => (moved.has(n.id) ? { ...n, pos: [n.pos[0] + dx, n.pos[1] + dy] as [number, number] } : n)),
          groups: drag.base.groups.map((g) => (sel.has(g.id) ? { ...g, pos: [g.pos[0] + dx, g.pos[1] + dy] as [number, number] } : g)),
        },
        { history: !drag.started },
      );
      if (!drag.started) setDrag({ ...drag, started: true });
    }
  }

  function onPointerUp(e: RPointerEvent) {
    if (drag?.kind === 'box') {
      const x0 = Math.min(drag.x0, drag.x1), x1 = Math.max(drag.x0, drag.x1);
      const y0 = Math.min(drag.y0, drag.y1), y1 = Math.max(drag.y0, drag.y1);
      const hit = doc.nodes.filter((n) => n.pos[0] < x1 && n.pos[0] + W > x0 && n.pos[1] < y1 && n.pos[1] + nodeHeight(byType.get(n.type), n) > y0).map((n) => n.id);
      setSel((s) => new Set([...(drag.add ? s : []), ...hit]));
    } else if (drag?.kind === 'wire') {
      const pin = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest<HTMLElement>('[data-pin]');
      if (pin) {
        const [id, name] = pin.dataset.pin!.split('|') as [string, string];
        connect(drag.from, drag.side, `${id}.${name}`, pin.dataset.side as 'left' | 'right');
      }
    }
    setDrag(null);
  }

  function onWheel(e: React.WheelEvent) {
    const r = ref.current!.getBoundingClientRect();
    const k = Math.min(2, Math.max(0.25, view.k * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    setView({ k, x: mx - ((mx - view.x) / view.k) * k, y: my - ((my - view.y) / view.k) * k });
  }

  const wires = doc.edges.map((e, i) => {
    const [fn, fp] = e.from.split('.') as [string, string];
    const [tn, tp] = e.to.split('.') as [string, string];
    const a = byId.get(fn), b = byId.get(tn);
    if (!a || !b) return null;
    const p = anchor(byType.get(a.type), a, 'right', fp);
    const q = anchor(byType.get(b.type), b, 'left', tp);
    const exec = pinsOf(byType.get(a.type), a).right.find((x) => x.name === fp)?.kind === 'exec';
    return <path key={i} d={curve(p, q)} className={`gw-wire${exec ? ' exec' : ''}`} onDoubleClick={() => set({ edges: doc.edges.filter((_, j) => j !== i) })} />;
  });

  const matches = menu ? defs.filter((d) => `${d.category} ${d.type}`.toLowerCase().includes(menu.q.toLowerCase())).slice(0, 14) : [];

  return (
    <div
      ref={ref}
      className="gw-canvas"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onKeyUp={(e) => e.key === ' ' && (spaceDown.current = false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onWheel={onWheel}
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-node],[data-group]')) return;
        const r = ref.current!.getBoundingClientRect();
        setMenu({ x: e.clientX - r.left, y: e.clientY - r.top, q: '' });
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="gw-toolbar">
        <button onClick={() => addGroup('group')} title="Group the selected nodes">Group</button>
        <button onClick={() => addGroup('comment')}>Comment</button>
        <button onClick={() => setView({ x: 40, y: 40, k: 1 })}>Reset view</button>
      </div>
      <div className="gw-world" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}>
        {doc.groups.map((g) => (
          <div key={g.id} className={`gw-group ${g.kind}${sel.has(g.id) ? ' sel' : ''}`} style={{ left: g.pos[0], top: g.pos[1], width: g.size[0], height: g.size[1] }}>
            <input
              data-group={g.id}
              className="gw-group-title"
              value={g.text}
              onChange={(e) => set({ groups: doc.groups.map((x) => (x.id === g.id ? { ...x, text: e.target.value } : x)) }, false)}
              onBlur={() => undefined}
            />
            <div className="gw-group-grip" data-group={g.id} />
          </div>
        ))}
        <svg className="gw-wires">
          {wires}
          {drag?.kind === 'wire' && <path className="gw-wire live" d={drag.side === 'right' ? curve([drag.ax, drag.ay], [drag.x, drag.y]) : curve([drag.x, drag.y], [drag.ax, drag.ay])} />}
        </svg>
        {doc.nodes.map((n) => {
          const def = byType.get(n.type);
          const { left, right } = pinsOf(def, n);
          const err = errors?.get(n.id);
          const settings = settingsOf(n);
          return (
            <div
              key={n.id}
              data-node={n.id}
              className={`gw-node${sel.has(n.id) ? ' sel' : ''}${err ? ' err' : ''}${active?.has(n.id) ? ' active' : ''}`}
              style={{ left: n.pos[0], top: n.pos[1], width: W }}
              title={err}
            >
              <div className={`gw-head cat-${(def?.category ?? 'unknown').toLowerCase()}`}>{def ? n.type.replace(/_/g, ' ') : `? ${n.type}`}</div>
              {settings.map((s) => (
                <div key={s} className="gw-set">
                  <span>{s}</span>
                  <input value={showValue(n.data[s])} onChange={(e) => setNode(n.id, (x) => ({ ...x, data: { ...x.data, [s]: parseValue(e.target.value) } }))} />
                </div>
              ))}
              <div className="gw-pins">
                <div className="gw-col">
                  {left.map((p) => {
                    const wired = doc.edges.some((e) => e.to === `${n.id}.${p.name}`);
                    return (
                      <div key={p.name} className="gw-row">
                        <i className={`gw-pin ${p.kind}${wired ? ' wired' : ''}`} data-pin={`${n.id}|${p.name}`} data-side="left" />
                        <span>{p.name}</span>
                        {p.kind === 'data' && !wired && (
                          <input
                            className="gw-lit"
                            placeholder={p.default}
                            value={showValue(n.data[p.name])}
                            onChange={(e) => {
                              const v = parseValue(e.target.value);
                              setNode(n.id, (x) => {
                                const data = { ...x.data };
                                if (v === undefined) delete data[p.name];
                                else data[p.name] = v;
                                return { ...x, data };
                              });
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="gw-col right">
                  {right.map((p) => (
                    <div key={p.name} className="gw-row">
                      <span>{p.name}</span>
                      <i className={`gw-pin ${p.kind}`} data-pin={`${n.id}|${p.name}`} data-side="right" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        {drag?.kind === 'box' && <div className="gw-box" style={{ left: Math.min(drag.x0, drag.x1), top: Math.min(drag.y0, drag.y1), width: Math.abs(drag.x1 - drag.x0), height: Math.abs(drag.y1 - drag.y0) }} />}
      </div>
      {menu && (
        <div className="gw-menu" style={{ left: menu.x, top: menu.y }}>
          <input
            autoFocus
            placeholder="Search nodes…"
            value={menu.q}
            onChange={(e) => setMenu({ ...menu, q: e.target.value })}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Escape') setMenu(null);
              if (e.key === 'Enter' && matches[0]) {
                addNode(matches[0], toWorld((ref.current?.getBoundingClientRect().left ?? 0) + menu.x, (ref.current?.getBoundingClientRect().top ?? 0) + menu.y));
                setMenu(null);
                ref.current?.focus();
              }
            }}
          />
          {matches.map((d) => (
            <button
              key={d.type}
              onClick={() => {
                const r = ref.current!.getBoundingClientRect();
                addNode(d, toWorld(r.left + menu.x, r.top + menu.y));
                setMenu(null);
                ref.current?.focus();
              }}
            >
              <em>{d.category}</em> {d.type.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function curve(a: [number, number], b: [number, number]): string {
  const dx = Math.max(40, Math.abs(b[0] - a[0]) / 2);
  return `M ${a[0]} ${a[1]} C ${a[0] + dx} ${a[1]}, ${b[0] - dx} ${b[1]}, ${b[0]} ${b[1]}`;
}
